import {
	Container,
	ContainerPadding,
	CTA,
	TextAlignment,
	TokenTextAppearance,
	Text,
	Breakpoints,
	Layout,
	styled,
} from '@volkswagen-onehub/components-core';
import React, { useContext, useEffect, useState } from 'react';
import { Vehicle, Dealers } from '@esportal/portal-service';
import { Calendar } from '@volkswagen-onehub/icons-core';
import { IPortalContext, PortalContext } from '../../../context/portal-context';
import { actionRedirect } from '../../../helpers/common-helpers';
import { CalendarStyledIcon } from '../ui/styled-icon';
import { useMyServicesTracker } from '../hooks/use-my-services-tracker';

const StyledContainer = styled(Container)`
	padding-top: var(--size-dynamic0130);
	padding-bottom: var(--size-dynamic0130);
	padding-right: var(--size-dynamic0050);
	padding-left: var(--size-dynamic0050);
`;

const CTAContainer = styled(Container)`
	& > div {
		justify-content: flex-start;
		padding-left: var(--size-static0);

		@media (max-width: 375px) {
			padding-top: var(--size-dynamic0100);
			justify-content: center;
		}
		@media (min-width: 376px) {
			padding-top: var(--size-dynamic0100);
			justify-content: center;
		}

		@media (min-width: 560px) {
			padding-left: var(--size-dynamic0250);
			padding-top: var(--size-dynamic0100);
			justify-content: flex-start;
		}

		@media (min-width: 960px) {
			padding-top: var(--size-dynamic0);
			justify-content: flex-end;
		}

		@media (min-width: 1190px) {
			padding-top: var(--size-static0);
			justify-content: flex-end;
		}
	}
`;

const TitleText = styled(Text)`
	text-align: left;

	@media (max-width: 559px) {
		text-align: center;
	}
`;

const SubtitleText = styled(Text)`
	@media (max-width: 559px) {
		display: none;
	}
`;

const useIsMobile = (maxWidth = 560): boolean => {
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		const mediaQuery = window.matchMedia(`(max-width: ${maxWidth}px)`);
		const handleChange = () => setIsMobile(mediaQuery.matches);

		handleChange();
		mediaQuery.addEventListener('change', handleChange);

		return () => mediaQuery.removeEventListener('change', handleChange);
	}, [maxWidth]);

	return isMobile;
};

const UseIs1280 = (minWidth = 1280): boolean => {
	const [is1280, setIs1280] = useState(false);

	useEffect(() => {
		const mediaQuery = window.matchMedia(`(min-width: ${minWidth}px)`);
		const handleChange = () => setIs1280(mediaQuery.matches);

		handleChange();
		mediaQuery.addEventListener('change', handleChange);

		return () => mediaQuery.removeEventListener('change', handleChange);
	}, [minWidth]);

	return is1280;
};

const UseIsMin560 = (minWidth = 560): boolean => {
	const [isMin560, setIsMin560] = useState(false);

	useEffect(() => {
		const mediaQuery = window.matchMedia(`(min-width: ${minWidth}px)`);
		const handleChange = () => setIsMin560(mediaQuery.matches);

		handleChange();
		mediaQuery.addEventListener('change', handleChange);

		return () => mediaQuery.removeEventListener('change', handleChange);
	}, [minWidth]);

	return isMin560;
};

export const ServicesCitaTaller = ({
	dealers,
	vehicleForm,
}: {
	vehicleForm: Vehicle;
	dealers: Dealers | null;
	portalContext: IPortalContext;
}): React.ReactElement => {
	const isMobile = useIsMobile();
	const is1280 = UseIs1280();
	const isMin560 = UseIsMin560();
	const { userInteractionService } = useContext(PortalContext);
	const kvpsCode = dealers?.kvpsCode;

	const { trackMyServicesPideCitaCtaClick } = useMyServicesTracker();

	return (
		<StyledContainer stretchContent>
			<Layout
				appearance={{
					[Breakpoints.default]: [
						{ name: '.', columns: 1 },
						{ name: 'a', columns: 22 },
						{ name: '.', columns: 1 },
						{ name: '.', columns: 1 },
						{ name: 'b', columns: 22 },
						{ name: '.', columns: 1 },
					],
					[Breakpoints.b560]: [
						{ name: '.', columns: 0 },
						{ name: 'a', columns: 24 },
						{ name: '.', columns: 0 },
						{ name: '.', columns: 0 },
						{ name: 'b', columns: 24 },
						{ name: '.', columns: 0 },
					],
					[Breakpoints.b960]: [
						{ name: '.', columns: 0 },
						{ name: 'a', columns: 16 },
						{ name: '.', columns: 0 },
						{ name: '.', columns: 0 },
						{ name: 'b', columns: 8 },
						{ name: '.', columns: 0 },
					],
				}}
			>
				<Container verticalAlign="flex-start" wrap={'never'}>
					<CalendarStyledIcon>
						<Calendar variant="default" />
					</CalendarStyledIcon>
					<Container
						wrap={'always'}
						padding={{
							left: isMin560 ? ContainerPadding.dynamic0100 : ContainerPadding.static0,
							right: isMin560 ? ContainerPadding.dynamic0200 : ContainerPadding.static0,
						}}
					>
						<TitleText
							appearance={is1280 ? TokenTextAppearance.headline300 : TokenTextAppearance.headline200}
							bold
							hyphens="auto"
						>
							¿Necesitas que cuiden de tu coche{dealers ? ` en ${dealers.commercialName}` : ''}?
						</TitleText>
						<Container
							padding={{
								top: ContainerPadding.static150,
								right: isMobile ? ContainerPadding.static100 : ContainerPadding.static0,
							}}
							wrap={'always'}
						>
							<SubtitleText appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.left}>
								Pide cita directamente con tu taller de preferencia.
							</SubtitleText>
						</Container>
					</Container>
				</Container>
				<CTAContainer verticalAlign="center" horizontalAlign={'flex-end'} data-testid="CTAContainer">
					<Container horizontalAlign={'center'} data-testid="Este">
						<CTA
							tag="button"
							emphasis="nbab"
							size="large"
							onClick={() => {
								trackMyServicesPideCitaCtaClick(vehicleForm);
								actionRedirect(
									userInteractionService,
									kvpsCode ? `action:es-cca-loader--open_layer?dealer=${kvpsCode}` : 'action:es-cca-loader--open_layer',
								);
							}}
							aria-label="aria label for CTA"
						>
							Pide cita en el taller
						</CTA>
					</Container>
				</CTAContainer>
			</Layout>
		</StyledContainer>
	);
};
